import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { SIDEBAR_BRAND_LOGO_SRC } from "../../constants/sidebarBrandLogo";
import {
  LOADING_CHART_CYCLE_HOLD_MS,
  LOADING_CHART_TILE_TRANSITION_MS,
} from "./loading-chart-timing";
import { getLoadingChartTileStyle, type LoadingChartTileKind } from "./loading-chart-tile-styles";
import "./loading-chart.css";

type MobileLoadingChartProps = {
  onRevealComplete?: () => void;
};

type Tile = {
  row: number;
  col: number;
  kind: LoadingChartTileKind;
};

type MobileFormation =
  | "uptrendA"
  | "uptrendB"
  | "downtrendA"
  | "downtrendB"
  | "rangeA"
  | "rangeB";
const MOBILE_FORMATIONS: MobileFormation[] = [
  "uptrendA",
  "uptrendB",
  "downtrendA",
  "downtrendB",
  "rangeA",
  "rangeB",
];

export function MobileLoadingChart({ onRevealComplete }: MobileLoadingChartProps) {
  const [visibleCount, setVisibleCount] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [viewport, setViewport] = useState({ width: 390, height: 844 });
  const [formation, setFormation] = useState<MobileFormation>("uptrendA");
  const firstCycleDoneRef = useRef(false);

  useEffect(() => {
    const updateViewport = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    const pick = MOBILE_FORMATIONS[Math.floor(Math.random() * MOBILE_FORMATIONS.length)] ?? "uptrendA";
    setFormation(pick);
  }, [cycle]);

  const { tiles, rows, cols, tileSize, gap, logoRevealIndex, logoRowStart, logoColStart, logoSpan } =
    useMemo(() => {
    const tileSize = 28;
    const gap = 4;
    const cols = Math.ceil((viewport.width + gap) / (tileSize + gap));
    const rows = Math.ceil((viewport.height + gap) / (tileSize + gap));
    const logoSpan = 2;
    const logoColStart = Math.floor((cols - logoSpan) / 2);
    const logoRowStart = Math.floor((rows - logoSpan) / 2);

    const chartByCell = new Map<string, LoadingChartTileKind>();
    const chartRows: number[] = [];
    const upStart = rows - Math.max(3, Math.floor(rows * 0.2));
    const upEnd = Math.max(1, Math.floor(rows * 0.12));
    const downStart = Math.max(1, Math.floor(rows * 0.12));
    const downEnd = rows - Math.max(3, Math.floor(rows * 0.2));
    const rangeMid = Math.floor(rows * 0.5);
    const isUp = formation.startsWith("uptrend");
    const isDown = formation.startsWith("downtrend");
    const isRange = formation.startsWith("range");
    const isVariantB = formation.endsWith("B");
    const startRow = isDown ? downStart : isRange ? rangeMid : upStart;
    const endRow = isDown ? downEnd : isRange ? rangeMid : upEnd;
    let currentRow = startRow;

    for (let col = 0; col < cols; col += 1) {
      const progress = cols <= 1 ? 1 : col / (cols - 1);
      const trendTarget = Math.round(startRow + (endRow - startRow) * progress);
      const config =
        isUp
          ? isVariantB
            ? { freq: 1.2, amp: 2.4, cycle: 7, pullbackMin: 3, pullbackMax: 5, hardPull: 4, hard: 3.4, soft: 2.5 }
            : { freq: 1.05, amp: 2.2, cycle: 8, pullbackMin: 4, pullbackMax: 6, hardPull: 5, hard: 3.2, soft: 2.4 }
          : isDown
            ? isVariantB
              ? { freq: 1.12, amp: 2.3, cycle: 7, pullbackMin: 2, pullbackMax: 4, hardPull: 3, hard: -3.2, soft: -2.2 }
              : { freq: 1.0, amp: 2.1, cycle: 8, pullbackMin: 3, pullbackMax: 5, hardPull: 4, hard: -3.0, soft: -2.1 }
            : isVariantB
              ? { freq: 1.3, amp: 1.9, cycle: 6, pullbackMin: 2, pullbackMax: 4, hardPull: 3, hard: 1.5, soft: 0.9 }
              : { freq: 1.15, amp: 1.6, cycle: 7, pullbackMin: 3, pullbackMax: 5, hardPull: 4, hard: 1.2, soft: 0.7 };
      const phase = progress * Math.PI * 2 * config.freq;
      const oscillation = Math.sin(phase) * config.amp;
      const cyclePos = col % config.cycle;
      const pullbackOffset =
        cyclePos >= config.pullbackMin && cyclePos <= config.pullbackMax
          ? cyclePos === config.hardPull
            ? config.hard
            : config.soft
          : 0;
      const value = Math.round(trendTarget + oscillation + pullbackOffset);

      currentRow = Math.max(1, Math.min(rows - 2, value));
      chartRows.push(currentRow);
    }

    for (let col = 0; col < cols; col += 1) {
      const curr = chartRows[col] ?? startRow;
      const prev = col === 0 ? curr : (chartRows[col - 1] ?? curr);
      const color: LoadingChartTileKind = curr <= prev ? "green" : "red";
      const minRow = Math.min(prev, curr);
      const maxRow = Math.max(prev, curr);

      for (let row = minRow; row <= maxRow; row += 1) {
        chartByCell.set(`${row}-${col}`, color);
      }
      chartByCell.set(`${curr}-${col}`, color);
    }

    const allTiles: Tile[] = [];
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        const isInLogoCutout =
          row >= logoRowStart &&
          row < logoRowStart + logoSpan &&
          col >= logoColStart &&
          col < logoColStart + logoSpan;
        if (isInLogoCutout) continue;
        const kind = chartByCell.get(`${row}-${col}`) ?? "grey";
        allTiles.push({ row, col, kind });
      }
    }

    const ordered = allTiles.sort((a, b) => {
      const aBottomRow = rows - 1 - a.row;
      const bBottomRow = rows - 1 - b.row;
      const aWave = a.col + aBottomRow;
      const bWave = b.col + bBottomRow;
      if (aWave !== bWave) return aWave - bWave;
      if (aBottomRow !== bBottomRow) return aBottomRow - bBottomRow;
      return a.col - b.col;
    });

    const logoIndex = ordered.findIndex((tile) => tile.col >= logoColStart && tile.row <= logoRowStart);
    const logoRevealIndex = logoIndex === -1 ? Math.floor(ordered.length * 0.5) : logoIndex;

    return { tiles: ordered, rows, cols, tileSize, gap, logoRevealIndex, logoRowStart, logoColStart, logoSpan };
  }, [formation, viewport.height, viewport.width]);

  useEffect(() => {
    setVisibleCount(0);
  }, [cycle, rows, cols]);

  useEffect(() => {
    const revealInterval = window.setInterval(() => {
      setVisibleCount((current) => {
        if (current >= tiles.length) {
          window.clearInterval(revealInterval);
          return current;
        }
        return Math.min(tiles.length, current + 14);
      });
    }, 18);

    return () => window.clearInterval(revealInterval);
  }, [cycle, tiles.length]);

  useEffect(() => {
    if (tiles.length === 0) {
      if (!firstCycleDoneRef.current) {
        firstCycleDoneRef.current = true;
        onRevealComplete?.();
      }
      return;
    }
    if (visibleCount < tiles.length) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (!firstCycleDoneRef.current) {
        firstCycleDoneRef.current = true;
        onRevealComplete?.();
      }
      setCycle((value) => value + 1);
    }, LOADING_CHART_TILE_TRANSITION_MS + LOADING_CHART_CYCLE_HOLD_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [onRevealComplete, tiles.length, visibleCount]);

  const showCenterLogo = visibleCount >= logoRevealIndex;

  return (
    <div
      className="tessera-loading-chart-overlay"
      data-tessera-route-loader
      aria-label="Loading chart animation"
      role="status"
    >
      <div className="tessera-loading-chart-stage">
        <div
          className="tessera-loading-chart-grid"
          style={{
            gap: `${gap}px`,
            gridTemplateColumns: `repeat(${cols}, ${tileSize}px)`,
          }}
        >
          <div
            className={`tessera-loading-chart-logo ${
              showCenterLogo ? "tessera-loading-chart-logo--visible" : "tessera-loading-chart-logo--hidden"
            }`}
            style={{
              gridColumn: `${logoColStart + 1} / span ${logoSpan}`,
              gridRow: `${logoRowStart + 1} / span ${logoSpan}`,
            }}
            aria-hidden="true"
          >
            <img
              src={SIDEBAR_BRAND_LOGO_SRC}
              alt=""
              className="tessera-loading-chart-logo-image"
              draggable={false}
            />
          </div>
          {tiles.map((tile, index) => {
            const isVisible = index < visibleCount;
            return (
              <div
                key={`${cycle}-${tile.row}-${tile.col}`}
                className={`tessera-loading-chart-tile ${
                  isVisible ? "tessera-loading-chart-tile--visible" : "tessera-loading-chart-tile--hidden"
                }`}
                style={{
                  width: `${tileSize}px`,
                  height: `${tileSize}px`,
                  gridRow: tile.row + 1,
                  gridColumn: tile.col + 1,
                  ...getLoadingChartTileStyle(tile.kind, "mobile"),
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
